import { Component, DoCheck, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Optional, Self, ViewChildren } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ErrorStateMatcher, mixinErrorState } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { clone } from 'lodash';
import { Subject } from 'rxjs';



const _MatInputBase = mixinErrorState(class {
  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    /** @docs-private */
    public ngControl: NgControl
  ) { }
});

@Component({
  selector: 'app-dictionary-mapper',
  templateUrl: './dictionary-mapper.component.html',
  providers: [{
    provide: MatFormFieldControl,
    useExisting: DictionaryMapperComponent
  }],
  host: {
    'class': 'd-block w-100',
    '[attr.id]': 'id',
    '[attr.data-placeholder]': 'placeholder',
    '[disabled]': 'disabled',
    '[required]': 'required',
    '[attr.readonly]': 'readonly || null',
    '[attr.aria-invalid]': '(empty && required) ? null : errorState',
    '[attr.aria-required]': 'required',
  }
})
export class DictionaryMapperComponent extends _MatInputBase implements OnInit, OnChanges, DoCheck, OnDestroy, ControlValueAccessor, MatFormFieldControl<any> {
  static nextId = 0;

  @HostBinding() id = `dictionary-mapper-input-${DictionaryMapperComponent.nextId++}`;
  @ViewChildren(MatSelect) selects: Array<MatSelect>;

  @Input() options: Array<string>;
  @Input() eventPrefix: string;

  selectedKey: any;
  enteredValue: string;

  // implements MatFormFieldControl
  value: undefined | Record<string, string>;
  focused: boolean = false;
  touched: boolean = false;
  shouldLabelFloat: boolean = false;
  stateChanges: Subject<any> = new Subject<void>();

  @Input()
  get name(): string {
    return this.ngControl.name as string;
  }
  set name(value: string) {
    this.ngControl.name = value;
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  get empty(): boolean {
    return this.isEmpty(this.value);
  }

  @Input('aria-describedby') userAriaDescribedBy: string;

  controlType: string = 'role-input';
  onChange = (_: any) => { };
  onTouched = () => { };

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private focusMonitor: FocusMonitor,
    @Optional() @Self() ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
  }

  ngOnChanges() {
    this.stateChanges.next();
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  // implements ControlValueAccessor
  writeValue(value: any): void {
    this.value = value;
    this.stateChanges.next();
  }

  // implements ControlValueAccessor
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // implements ControlValueAccessor
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // implements ControlValueAccessor
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // implements MatFormFieldControl
  onContainerClick(event: MouseEvent): void {
    this.selects.some((item): boolean => {
      if (!item.value) {
        item.focus();
        return true;
      }
      return false;
    });
  }

  // implements MatFormFieldControl
  setDescribedByIds(ids: string[]): void {
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
    if (!control.errors && nextElement) {
      this.focusMonitor.focusVia(nextElement, 'program');
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this.focusMonitor.focusVia(prevElement, 'program');
    }
  }

  addAttribute() {
    let value = clone(this.value);

    if (!value) {
      value = {};
    }
    value[this.selectedKey] = this.enteredValue;
    this.selectedKey = null;
    this.enteredValue = '';
    this.writeValue(value);
  }

  removeAttribute(key: string) {
    const value = clone(this.value);

    delete value[key];
    this.writeValue(value);
  }

  activeOptions(): string[] {
    return this.value ? this.options.filter(o => !(o in this.value)) : this.options;
  }

  isEmpty(obj: any) {
    return !obj || Object.keys(obj).length === 0;
  }

  trackKey(index: number, item: any) {
    return item.key;
  }
}
